import {
  ETHER,
  TokenAmount,
  JSBI,
  Pair,
  Percent,
  CurrencyAmount
} from 'flashliquidity-sdk'
import { ETHER as ETH, USDC_MULTI } from '../../constants'
import { darken } from 'polished'
import React, { useEffect, useRef, useState } from 'react'
import { ChevronDown, ChevronUp } from 'react-feather'
import { Link } from 'react-router-dom'
import { Text } from 'rebass'
import styled from 'styled-components/macro'
import { useTotalSupply } from '../../data/TotalSupply'

import { useActiveWeb3React } from '../../hooks'
import { useTokenBalance } from '../../state/wallet/hooks'
import { /**ExternalLink,*/ TYPE } from '../../theme'
import { currencyId } from '../../utils/currencyId'
import { unwrappedToken } from '../../utils/wrappedCurrency'
import { ButtonPrimary, /**ButtonSecondary,*/ ButtonEmpty } from '../Button'
import Card, { GreyCard } from '../Card'
import { AutoColumn } from '../Column'
import CurrencyLogo from '../CurrencyLogo'
import DoubleCurrencyLogo from '../DoubleLogo'
import { RowBetween, RowFixed } from '../Row'
import { Dots } from '../swap/styleds'
import useUSDCPrice from '../../utils/useUSDCPrice'

export const FixedHeightRow = styled(RowBetween)`
  height: 20px;
`

export const HoverCard = styled(Card)`
  border: 1px solid transparent;
  :hover {
    border: 1px solid ${({ theme }) => darken(0.06, theme.bg2)};
  }
`
const StyledPositionCard = styled(Card)`
  border: 2px solid ${({ theme }) => theme.bg3};
  background: ${({ theme }) =>
    `linear-gradient(135deg, ${theme.bg2}, ${theme.bg3})`};
  position: relative;
  overflow: hidden;
`

function addDotsToNumber(num: string) {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

interface PositionCardProps {
  pair: Pair
  showUnwrapped?: boolean
  border?: string
  onLiquidityValueChange?: (value: number) => void // New prop
}

export function MinimalPositionCard({
  pair,
  showUnwrapped = false,
  border
}: PositionCardProps) {
  const { account, chainId } = useActiveWeb3React()

  const currency0 = showUnwrapped ? pair.token0 : unwrappedToken(pair.token0)
  const currency1 = showUnwrapped ? pair.token1 : unwrappedToken(pair.token1)
  const [showMore, setShowMore] = useState(false)

  const userPoolBalance = useTokenBalance(
    account ?? undefined,
    pair.liquidityToken
  )
  const totalPoolTokens = useTotalSupply(pair.liquidityToken)

  const poolTokenPercentage =
    !!userPoolBalance &&
    !!totalPoolTokens &&
    JSBI.greaterThanOrEqual(totalPoolTokens.raw, userPoolBalance.raw)
      ? new Percent(userPoolBalance.raw, totalPoolTokens.raw)
      : undefined

  const [token0Deposited, token1Deposited] =
    !!pair &&
    !!totalPoolTokens &&
    !!userPoolBalance &&
    // this condition is a short-circuit in the case where useTokenBalance updates sooner than useTotalSupply
    JSBI.greaterThanOrEqual(totalPoolTokens.raw, userPoolBalance.raw)
      ? [
          pair.getLiquidityValue(
            pair.token0,
            totalPoolTokens,
            userPoolBalance,
            false
          ),
          pair.getLiquidityValue(
            pair.token1,
            totalPoolTokens,
            userPoolBalance,
            false
          )
        ]
      : [undefined, undefined]

  return (
    <>
      {userPoolBalance &&
      JSBI.greaterThan(userPoolBalance.raw, JSBI.BigInt(0)) ? (
        <GreyCard border={border}>
          <AutoColumn gap="12px">
            <FixedHeightRow>
              <RowFixed>
                <Text fontWeight={500} fontSize={14}>
                  Your position
                </Text>
              </RowFixed>
            </FixedHeightRow>
            <FixedHeightRow onClick={() => setShowMore(!showMore)}>
              <RowFixed>
                <DoubleCurrencyLogo
                  chainId={chainId!}
                  currency0={currency0}
                  currency1={currency1}
                  margin={true}
                  size={20}
                />
                <Text fontWeight={500} fontSize={16}>
                  {currency0.symbol}/{currency1.symbol}
                </Text>
              </RowFixed>
              <RowFixed>
                <Text fontWeight={500} fontSize={16}>
                  {userPoolBalance ? userPoolBalance.toSignificant(4) : '-'}
                </Text>
              </RowFixed>
            </FixedHeightRow>
            <AutoColumn gap="4px">
              <FixedHeightRow>
                <Text fontSize={14} fontWeight={500}>
                  Your pool share:
                </Text>
                <Text fontSize={14} fontWeight={500}>
                  {poolTokenPercentage
                    ? poolTokenPercentage.toFixed(6) + '%'
                    : '-'}
                </Text>
              </FixedHeightRow>
              <FixedHeightRow>
                <Text fontSize={14} fontWeight={500}>
                  {currency0.symbol}:
                </Text>
                {token0Deposited ? (
                  <RowFixed>
                    <Text fontSize={14} fontWeight={500} marginLeft={'6px'}>
                      {token0Deposited?.toSignificant(6)}
                    </Text>
                  </RowFixed>
                ) : (
                  '-'
                )}
              </FixedHeightRow>
              <FixedHeightRow>
                <Text fontSize={14} fontWeight={500}>
                  {currency1.symbol}:
                </Text>
                {token1Deposited ? (
                  <RowFixed>
                    <Text fontSize={14} fontWeight={500} marginLeft={'6px'}>
                      {token1Deposited?.toSignificant(6)}
                    </Text>
                  </RowFixed>
                ) : (
                  '-'
                )}
              </FixedHeightRow>
            </AutoColumn>
          </AutoColumn>
        </GreyCard>
      ) : (
        <Card border="1px solid grey">
          <TYPE.subHeader style={{ textAlign: 'left' }}>
            By adding liquidity you&apos;ll earn 0.30% of all trades on this
            pair proportional to your share of the pool. Fees are added to the
            pool, accrue in real time and can be claimed by withdrawing your
            liquidity.
          </TYPE.subHeader>
        </Card>
      )}
    </>
  )
}

export function MicroPositionCard({
  pair,
  showUnwrapped = false,
  onLiquidityValueChange
}: PositionCardProps) {
  const { account, chainId } = useActiveWeb3React()
  const previousValueRef = useRef<number | null>(null)

  const currency0 = showUnwrapped ? pair.token0 : unwrappedToken(pair.token0)
  const currency1 = showUnwrapped ? pair.token1 : unwrappedToken(pair.token1)
  const [showMore, setShowMore] = useState(false)
  let isWETH
  if (chainId === 137) {
    isWETH =
      currency0 === ETHER[chainId!] || currency1 === ETH
        ? pair.token1
        : pair.token0
  } else {
    isWETH =
      currency1 === ETHER[chainId!] || currency1 === USDC_MULTI[chainId!]
        ? pair.token0
        : pair.token1
  }
  const userPoolBalance = useTokenBalance(
    account ?? undefined,
    pair.liquidityToken
  )
  const totalPoolTokens = useTotalSupply(pair.liquidityToken)

  const USDPrice = useUSDCPrice(isWETH)

  let valueOfUserStakedAmountInUSDC: CurrencyAmount | undefined
  let valueOfUserStakedAmountInWETH: TokenAmount | undefined
  if (totalPoolTokens) {
    // let returnOverMonth: Percent = new Percent('0')
    if (userPoolBalance) {
      // take the total amount of LP tokens staked, multiply by ETH value of all LP tokens, divide by all LP tokens
      valueOfUserStakedAmountInWETH = new TokenAmount(
        isWETH!,
        JSBI.divide(
          JSBI.multiply(
            JSBI.multiply(userPoolBalance.raw, pair.reserveOf(isWETH!).raw),
            JSBI.BigInt(2) // this is b/c the value of LP shares are ~double the value of the WETH they entitle owner to
          ),
          totalPoolTokens.raw
        )
      )
    }
    // get the USD value of staked WETH
    valueOfUserStakedAmountInUSDC = USDPrice?.quote(
      valueOfUserStakedAmountInWETH!,
      chainId!
    )
  }

  useEffect(() => {
    const currentValue = valueOfUserStakedAmountInUSDC
      ? parseFloat(valueOfUserStakedAmountInUSDC.toSignificant(6))
      : 0

    if (previousValueRef.current !== null && onLiquidityValueChange) {
      onLiquidityValueChange(currentValue - previousValueRef.current)
    } else if (onLiquidityValueChange) {
      onLiquidityValueChange(currentValue)
    }

    previousValueRef.current = currentValue
  }, [valueOfUserStakedAmountInUSDC, onLiquidityValueChange])

  return (
    <>
      <AutoColumn>
        <FixedHeightRow
          marginTop={'6px'}
          onClick={() => setShowMore(!showMore)}
        >
          <RowFixed>
            <DoubleCurrencyLogo
              chainId={chainId!}
              currency0={currency0}
              currency1={currency1}
              margin={true}
              size={20}
            />
            <Text fontWeight={500} fontSize={16}>
              {currency0.symbol}/{currency1.symbol}
            </Text>
          </RowFixed>
          <RowFixed>
            <Text fontWeight={500} fontSize={16}>
              {valueOfUserStakedAmountInUSDC
                ? addDotsToNumber(valueOfUserStakedAmountInUSDC.toFixed(2)) +
                  ' $'
                : '-'}
            </Text>
          </RowFixed>
        </FixedHeightRow>
      </AutoColumn>
    </>
  )
}

export default function FullPositionCard({ pair, border }: PositionCardProps) {
  const { account, chainId } = useActiveWeb3React()

  const currency0 = unwrappedToken(pair.token0)
  const currency1 = unwrappedToken(pair.token1)
  let isWETH
  if (chainId === 137) {
    isWETH =
      currency1 === ETHER[chainId!] || currency1 === ETH
        ? pair.token1
        : pair.token0
  } else {
    isWETH =
      currency0 === ETHER[chainId!] || currency0 === USDC_MULTI[chainId!]
        ? pair.token0
        : pair.token1
  }
  const [showMore, setShowMore] = useState(false)

  const userPoolBalance = useTokenBalance(
    account ?? undefined,
    pair.liquidityToken
  )
  const totalPoolTokens = useTotalSupply(pair.liquidityToken)

  const poolTokenPercentage =
    !!userPoolBalance &&
    !!totalPoolTokens &&
    JSBI.greaterThanOrEqual(totalPoolTokens.raw, userPoolBalance.raw)
      ? new Percent(userPoolBalance.raw, totalPoolTokens.raw)
      : undefined

  const [token0Deposited, token1Deposited] =
    !!pair &&
    !!totalPoolTokens &&
    !!userPoolBalance &&
    // this condition is a short-circuit in the case where useTokenBalance updates sooner than useTotalSupply
    JSBI.greaterThanOrEqual(totalPoolTokens.raw, userPoolBalance.raw)
      ? [
          pair.getLiquidityValue(
            pair.token0,
            totalPoolTokens,
            userPoolBalance,
            false
          ),
          pair.getLiquidityValue(
            pair.token1,
            totalPoolTokens,
            userPoolBalance,
            false
          )
        ]
      : [undefined, undefined]
  // get the USD value of staked WETH
  const USDPrice = useUSDCPrice(isWETH)

  let valueOfUserStakedAmountInUSDC
  let valueOfUserStakedAmountInWETH: TokenAmount | undefined
  if (totalPoolTokens) {
    // let returnOverMonth: Percent = new Percent('0')
    if (userPoolBalance) {
      // take the total amount of LP tokens staked, multiply by ETH value of all LP tokens, divide by all LP tokens
      valueOfUserStakedAmountInWETH = new TokenAmount(
        isWETH!,
        JSBI.divide(
          JSBI.multiply(
            JSBI.multiply(userPoolBalance.raw, pair.reserveOf(isWETH!).raw),
            JSBI.BigInt(2) // this is b/c the value of LP shares are ~double the value of the WETH they entitle owner to
          ),
          totalPoolTokens.raw
        )
      )
    }
    // get the USD value of staked WETH
    valueOfUserStakedAmountInUSDC = USDPrice?.quote(
      valueOfUserStakedAmountInWETH!,
      chainId!
    )
  }
  return (
    <StyledPositionCard border={border}>
      <AutoColumn gap="12px">
        <FixedHeightRow>
          <RowFixed>
            <DoubleCurrencyLogo
              chainId={chainId!}
              currency0={currency0}
              currency1={currency1}
              margin={true}
              size={28}
            />
            <Text fontWeight={500} fontSize={20}>
              {!currency0 || !currency1 ? (
                <Dots>Loading</Dots>
              ) : (
                `${currency0.symbol}/${currency1.symbol}`
              )}
            </Text>
          </RowFixed>

          <RowFixed gap="8px">
            <ButtonEmpty
              padding="6px 8px"
              borderRadius="12px"
              width="fit-content"
              onClick={() => setShowMore(!showMore)}
            >
              {showMore ? (
                <>
                  {' '}
                  Manage
                  <ChevronUp size="20" style={{ marginLeft: '10px' }} />
                </>
              ) : (
                <>
                  Manage
                  <ChevronDown size="20" style={{ marginLeft: '10px' }} />
                </>
              )}
            </ButtonEmpty>
          </RowFixed>
        </FixedHeightRow>

        {showMore && (
          <AutoColumn gap="8px">
            <FixedHeightRow>
              <Text fontSize={16} fontWeight={500}>
                Your liquidity value:
              </Text>
              <Text fontSize={16} fontWeight={500}>
                {valueOfUserStakedAmountInUSDC
                  ? valueOfUserStakedAmountInUSDC.toFixed(2) + '$'
                  : '-'}
              </Text>
            </FixedHeightRow>
            <FixedHeightRow>
              <Text fontSize={16} fontWeight={500}>
                Your pool tokens:
              </Text>
              <Text fontSize={16} fontWeight={500}>
                {userPoolBalance ? userPoolBalance.toSignificant(4) : '-'}
              </Text>
            </FixedHeightRow>
            <FixedHeightRow>
              <Text fontSize={16} fontWeight={500}>
                Your pool share:
              </Text>
              <Text fontSize={16} fontWeight={500}>
                {poolTokenPercentage
                  ? poolTokenPercentage.toFixed(2) + '%'
                  : '-'}
              </Text>
            </FixedHeightRow>
            <FixedHeightRow>
              <RowFixed>
                <Text fontSize={16} fontWeight={500}>
                  Pooled {currency0.symbol}:
                </Text>
              </RowFixed>
              {token0Deposited ? (
                <RowFixed>
                  <Text fontSize={16} fontWeight={500} marginLeft={'6px'}>
                    {token0Deposited?.toSignificant(6)}
                  </Text>
                  <CurrencyLogo
                    chainId={chainId!}
                    size="20px"
                    style={{ marginLeft: '8px' }}
                    currency={currency0}
                  />
                </RowFixed>
              ) : (
                '-'
              )}
            </FixedHeightRow>

            <FixedHeightRow>
              <RowFixed>
                <Text fontSize={16} fontWeight={500}>
                  Pooled {currency1.symbol}:
                </Text>
              </RowFixed>
              {token1Deposited ? (
                <RowFixed>
                  <Text fontSize={16} fontWeight={500} marginLeft={'6px'}>
                    {token1Deposited?.toSignificant(6)}
                  </Text>
                  <CurrencyLogo
                    chainId={chainId!}
                    size="20px"
                    style={{ marginLeft: '8px' }}
                    currency={currency1}
                  />
                </RowFixed>
              ) : (
                '-'
              )}
            </FixedHeightRow>

            {/*<ButtonSecondary padding="8px" borderRadius="8px">
              <ExternalLink
                style={{ width: '100%', textAlign: 'center' }}
                href={`https://uniswap.info/account/${account}`}
              >
                View accrued fees and analytics<span style={{ fontSize: '11px' }}>↗</span>
              </ExternalLink>
              </ButtonSecondary>*/}
            <RowBetween marginTop="10px">
              <ButtonPrimary
                padding="8px"
                borderRadius="8px"
                as={Link}
                to={`/add/${currencyId(chainId!, currency0)}/${currencyId(
                  chainId!,
                  currency1
                )}`}
                width="48%"
              >
                Add
              </ButtonPrimary>
              <ButtonPrimary
                padding="8px"
                borderRadius="8px"
                as={Link}
                width="48%"
                to={`/remove/${currencyId(chainId!, currency0)}/${currencyId(
                  chainId!,
                  currency1
                )}`}
              >
                Remove
              </ButtonPrimary>
            </RowBetween>
          </AutoColumn>
        )}
      </AutoColumn>
    </StyledPositionCard>
  )
}
